import { Row as RTRow } from '@tanstack/react-table';
import { VirtualItem } from '@tanstack/react-virtual';
import { useEffect, useRef } from 'react';
import { useTheme } from 'styled-components';
import { STATIC_DATA_ROW_STYLES } from '../../constants';
import { IDataTableProps, TableVirtualizer } from '../../types';
import { Row } from '../Row';
import { SubComponentBodyCell } from './SubComponentBodyCell';

interface ISubComponentRowProps<T> {
  virtualRow: VirtualItem<Element> | undefined;
  parentRowHeight: number;
  virtualizer: TableVirtualizer;
  ExpandedRowComponent: NonNullable<IDataTableProps<T>['ExpandedRowComponent']>;
  row: RTRow<T>;
}

export const SubComponentRow = <T extends object>({
  virtualRow,
  parentRowHeight,
  virtualizer,
  ExpandedRowComponent,
  row,
}: ISubComponentRowProps<T>) => {
  const theme = useTheme();

  const rowRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (!virtualRow || !rowRef.current) return;

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (!rowRef.current) return;

        const elementHeight = rowRef.current.getBoundingClientRect().height;

        const calculatedSize = elementHeight + parentRowHeight;

        if (virtualRow.size === calculatedSize) return;

        virtualizer.resizeItem(virtualRow.index, calculatedSize);
      });
    });

    observer.observe(rowRef.current);

    return () => {
      observer.disconnect();
    };
  }, [virtualRow, parentRowHeight]);

  return (
    <Row
      style={{
        ...(virtualRow && {
          ...STATIC_DATA_ROW_STYLES,
          transform: `translateY(${virtualRow.start + parentRowHeight}px)`,
        }),
        backgroundColor: theme.colorsV2.gray[200],
      }}
      disableHover
      ref={rowRef}
      key={row.id}
      subject={`sub-component-row`}
    >
      <SubComponentBodyCell>
        <ExpandedRowComponent row={row} />
      </SubComponentBodyCell>
    </Row>
  );
};
